import { Job } from '@/types/jobs-types';

export interface GroupedJob {
  // Primary job to display (first occurrence)
  primaryJob: Job;
  // All locations for this job (including primary)
  locations: Array<{
    job: Job;
    city: string;
    state: string;
    locationKey: string;
  }>;
}

/**
 * Normalizes a string for grouping comparison:
 * - trims whitespace
 * - lowercases
 * - replaces accented characters with ASCII equivalents
 * - removes numbers
 * - removes special characters (keeps only letters and spaces)
 * - collapses multiple spaces into one
 */
function normalizeForGrouping(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .replace(/[0-9]/g, '')           // remove numbers
    .replace(/[^a-z\s]/g, '')        // remove special characters
    .replace(/\s+/g, ' ')            // collapse spaces
    .trim();
}

/**
 * Groups jobs by normalized company, title, and location key,
 * collecting different locations under a single card.
 */
export function groupJobsByCompanyAndTitle(jobs: Job[]): GroupedJob[] {
  const groupMap = new Map<string, GroupedJob>();

  jobs.forEach((job) => {
    const company = normalizeForGrouping(job.company);
    const title = normalizeForGrouping(job.title);
    const groupKey = `${company}|||${title}`;

    // Extract location info
    const city = typeof job.location === 'string'
      ? job.city || job.location.split(',')[0]?.trim() || ''
      : job.location?.city || job.city || '';
    const state = typeof job.location === 'string'
      ? job.state || job.location.split(',')[1]?.trim() || ''
      : job.location?.state || job.state || '';

    const locationKey = normalizeForGrouping(`${city} ${state}`);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        primaryJob: job,
        locations: [{
          job,
          city,
          state,
          locationKey
        }]
      });
    } else {
      const group = groupMap.get(groupKey)!;
      const locationExists = group.locations.some(
        loc => loc.locationKey === locationKey
      );

      if (!locationExists) {
        group.locations.push({
          job,
          city,
          state,
          locationKey
        });
      }
    }
  });

  return Array.from(groupMap.values());
}

/**
 * Checks if a job array has any groups (duplicate company+title combinations)
 */
export function hasJobGroups(jobs: Job[]): boolean {
  const seen = new Set<string>();

  for (const job of jobs) {
    const company = normalizeForGrouping(job.company);
    const title = normalizeForGrouping(job.title);
    const key = `${company}|||${title}`;

    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
  }

  return false;
}
